import { useCallback, useState, Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { SMBeamModel, LimitStateSelection, TabType, LoadCaseStorageRequest, LoadEntryStorageRequest, LoadCombinationStorageRequest, MultiLimitStateAnalysisResponse, MultiLimitStateResponseResult } from '../types'
import { resolveMaterialPayload } from '../materialConfig'
import { beamApiBase } from '@structura/services'
import { TranslationKey, TranslateParams } from '@structura/i18n'

// Extract the backend's error message from a failed response. The backend throws with
// copy-pasteable diagnostics (missing γM, invalid section class, ...) serialized in the body —
// surfacing only the HTTP status line would hide the actual cause from the user.
async function extractBackendError(response: Response): Promise<string>
{
  let errorText = ''
  try
  {
    errorText = await response.text()
  }
  catch
  {
    return `HTTP ${response.status}: ${response.statusText}`
  }

  let errorData: any
  try
  {
    errorData = JSON.parse(errorText)
  }
  catch
  {
    errorData = { error: errorText }
  }

  return errorData.error || errorData.Error || errorData.message || errorData.Message || `HTTP ${response.status}: ${response.statusText}`
}

export interface MaterialSelection
{
  currentMaterial: string
  nationalAnnex: 'Standard' | 'Norwegian'
  selectedSteelGrade?: string
  selectedSteelSubgrade?: string
  steelThickness?: number | ''
  selectedConcreteClass?: string
  selectedReinfClass?: string
  selectedPrestressClass?: string
  timberKind?: string
  selectedTimberClass?: string
  timberOptions?: unknown[]
  timberLoadDuration?: string
  timberServiceClass?: 1 | 2 | 3
  concreteRelativeHumidity?: number
  concreteNotionalSize?: number
  concreteLoadingAge?: number
  concreteCreepCoefficient?: number
  concreteEffectiveModulus?: number
  materialDetails?: any  // Required: auto-fetched from database when material selection changes
}

export interface AnalysisDependencies
{
  model: SMBeamModel
  setModel: Dispatch<SetStateAction<SMBeamModel>>
  material: MaterialSelection
  codeRequirements: unknown
  selectedLimitState: LimitStateSelection
  getSessionHeaders: () => Record<string, string>
  setActiveStep: (step: TabType) => void
  t: (key: TranslationKey, params?: TranslateParams) => string
}

export interface AnalysisActions
{
  result: any
  setResult: Dispatch<SetStateAction<any>>
  // Drops every analysis/response result and marks in-flight requests stale: call on any model
  // edit that changes what an analysis would compute (geometry, loads, ...).
  invalidateResults: () => void
  loading: boolean
  analyzeBeam: () => Promise<void>
  analyzingAll: boolean
  analyzeAllLimitStates: () => Promise<void>
  analyzingTimoshenko: boolean
  analyzeTimoshenko: () => Promise<void>
  analysisTheory: 'EulerBernoulli' | 'Timoshenko'
  multiLimitStateResult: MultiLimitStateAnalysisResponse | null
  lastAnalysisSummary: string[] | null
  selectedCombinationId: string
  setSelectedCombinationId: (identifier: string) => void
  calculatingResponse: boolean
  calculateResponse: () => Promise<void>
  calculatingSecondOrderEffects: boolean
  calculateSecondOrderEffects: (options?: { combinationId?: string }) => Promise<void>
  secondOrderEffectsResult: any
  calculatingDynamics: boolean
  calculateDynamics: () => Promise<void>
  dynamicsResult: any
}

function buildCrossSection(model: SMBeamModel)
{
  return {
    name: model.crossSection.name,
    type: model.crossSection.sectionType || 'I-beam',
    height: model.crossSection.height,
    width: model.crossSection.width,
    webThickness: model.crossSection.webThickness || 0,
    flangeThickness: model.crossSection.flangeThickness || 0,
    area: model.crossSection.area,
    inertiaY: model.crossSection.inertia,
    inertiaZ: model.crossSection.inertiaZ ?? model.crossSection.inertia * 0.1,
    sectionClass: model.crossSection.sectionClass || 'auto',
    elasticModulusY: model.crossSection.elasticModulusY,
    plasticModulusY: model.crossSection.plasticModulusY,
    elasticModulusZ: model.crossSection.elasticModulusZ,
    plasticModulusZ: model.crossSection.plasticModulusZ,
    reinforcementRatio: model.crossSection.reinforcementRatio,
    effectiveCover: model.crossSection.effectiveCover,
    reinforcementAreaBottom: model.crossSection.reinforcementAreaBottom,
    reinforcementLeverArmBottom: model.crossSection.reinforcementLeverArmBottom,
    effectiveDepth: model.crossSection.effectiveDepth,
    elasticMomentCapacityKNm: model.crossSection.elasticMomentCapacityKNm,
    plasticMomentCapacityKNm: model.crossSection.plasticMomentCapacityKNm,
    reinforcedMomentCapacityKNm: model.crossSection.reinforcedMomentCapacityKNm,
    shearCorrectionFactor: model.crossSection.shearCorrectionFactor
  }
}

export function buildLoadCasesPayload(model: SMBeamModel): LoadCaseStorageRequest[]
{
  // id is deliberately OMITTED: the backend binds it as Guid?, and UI load identifiers are
  // textual ("selfweight-span-1") — sending them fails model binding and 400s the analysis.
  const toEntry = (load: SMBeamModel['spans'][number]['loads'][number]): LoadEntryStorageRequest => ({
      clientId: load.id,
      spanId: load.spanId,
      loadType: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
      pointLoad: load.pointLoad,
      pointLoadPosition: load.pointLoadPosition,
      normalForce: load.normalForce,
      normalForcePosition: load.normalForcePosition,
      normalForceEccentricity: load.normalForceEccentricity,
      uniformIntensity: load.uniformIntensity,
      startIntensity: load.startIntensity,
      endIntensity: load.endIntensity,
      startPosition: load.startPosition,
      endPosition: load.endPosition,
      axialForceEnd: load.axialForceEnd
  })

  // Span loads are the analysis source of truth. Template load cases supply useful names, but
  // users can subsequently edit/remove those loads in the Loads tab; persisting the untouched
  // template copies would make the report disagree with the analysis. Reconcile by load id and
  // category, then place every unassigned current load in a category case.
  const currentLoads = model.spans.flatMap(span => span.loads)
  const assignedLoadIds = new Set<string>()
  const payload: LoadCaseStorageRequest[] = []

  model.loadCases.forEach(loadCase => {
    const configuredIds = new Set(loadCase.loads.map(load => load.id))
    const matchingLoads = currentLoads.filter(load =>
      configuredIds.has(load.id) && (load.category ?? 'Variable') === loadCase.type)
    if (matchingLoads.length === 0) {
      return
    }
    matchingLoads.forEach(load => assignedLoadIds.add(load.id))
    payload.push({
      clientId: loadCase.id,
      name: loadCase.name,
      category: loadCase.type,
      isActive: loadCase.isActive,
      displayOrder: payload.length,
      loads: matchingLoads.map(toEntry)
    })
  })

  const unassignedByCategory = new Map<string, typeof currentLoads>()
  currentLoads.forEach(load => {
    if (assignedLoadIds.has(load.id)) {
      return
    }
    const category = load.category ?? 'Variable'
    const categoryLoads = unassignedByCategory.get(category) ?? []
    categoryLoads.push(load)
    unassignedByCategory.set(category, categoryLoads)
  })

  unassignedByCategory.forEach((loads, category) => {
    const clientId = `analysis-${category.toLowerCase()}`
    payload.push({
      clientId,
      name: category,
      category,
      isActive: true,
      displayOrder: payload.length,
      loads: loads.map(toEntry)
    })
  })

  return payload
}

export default function useAnalysisActions(dependencies: AnalysisDependencies): AnalysisActions
{
  const { model, material, codeRequirements, selectedLimitState, getSessionHeaders, setActiveStep, t } = dependencies

  const [result, setResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [analyzingAll, setAnalyzingAll] = useState(false)
  const [analyzingTimoshenko, setAnalyzingTimoshenko] = useState(false)
  const [analysisTheory, setAnalysisTheory] = useState<'EulerBernoulli' | 'Timoshenko' | null>(null)
  const [multiLimitStateResult, setMultiLimitStateResult] = useState<MultiLimitStateAnalysisResponse | null>(null)
  const [lastAnalysisSummary, setLastAnalysisSummary] = useState<string[] | null>(null)
  const [, setMultiLimitStateResponseResult] = useState<MultiLimitStateResponseResult | null>(null)
  const [calculatingResponse, setCalculatingResponse] = useState(false)
  const [calculatingSecondOrderEffects, setCalculatingSecondOrderEffects] = useState(false)
  const [secondOrderEffectsResult, setSecondOrderEffectsResult] = useState<any>(null)
  const [calculatingDynamics, setCalculatingDynamics] = useState(false)
  const [dynamicsResult, setDynamicsResult] = useState<any>(null)
  const [selectedCombinationId, setSelectedCombinationId] = useState<string>('')

  // Model revision: bumped by invalidateResults() on every relevant model edit. Each request
  // captures it when it starts and commits only if it is unchanged, so a response that arrives
  // after an edit can never populate results for a model that no longer exists.
  const modelRevision = useRef<number>(0)

  const invalidateResults = useCallback((): void => {
    modelRevision.current += 1
    setResult(null)
    setMultiLimitStateResult(null)
    setMultiLimitStateResponseResult(null)
    setLastAnalysisSummary(null)
    setSecondOrderEffectsResult(null)
    setDynamicsResult(null)
  }, [])

  // True when the model changed since `revision` was captured — the caller must drop its result.
  const isStale = (revision: number, what: string): boolean => {
    if (modelRevision.current === revision) {
      return false
    }
    console.warn(`${what} finished for an outdated model (revision ${revision}, now ${modelRevision.current}); result discarded`)
    return true
  }

  const resolveAnalysisId = (analysis: any): string | undefined => {
    if (!analysis) {
      return undefined
    }
    return analysis.analysisId ?? analysis.AnalysisId ?? analysis.analysisID ?? analysis.id ?? undefined
  }

  const analyzeBeam = useCallback(async () => {
    const revision: number = modelRevision.current
    setLoading(true)
    try
    {
      const hasAxialLoads = model.spans.some(span =>
        span.loads.some(load => load.type === 'AxialForce' && Math.abs(load.normalForce) > 0.001)
      )

      if (hasAxialLoads)
      {
        const hasAxialSupport = model.nodes.some(node => node.axialFixed && node.supportType !== 'Free')
        if (!hasAxialSupport)
        {
          alert(t('beam.analysis.alert.axialNoSupport'))
          setLoading(false)
          return
        }
      }

      const isSingleSpan = model.spans.length === 1
      let beamAnalysisRequest: any

      if (isSingleSpan)
      {
        const span = model.spans[0]
        const leftNode = model.nodes.find(node => node.id === span.leftNodeId)
        const rightNode = model.nodes.find(node => node.id === span.rightNodeId)

        beamAnalysisRequest = {
          length: span.length,
          e: span.E,
          a: span.A,
          i: span.I,
          leftSupportType: leftNode?.supportType || 'Free',
          rightSupportType: rightNode?.supportType || 'Free',
          leftBearingLengthMm: leftNode?.bearingLengthMm,
          rightBearingLengthMm: rightNode?.bearingLengthMm,
          loads: span.loads.map(load => ({
            type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
            category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
            pointLoad: load.pointLoad,
            pointLoadPosition: load.pointLoadPosition,
            normalForce: load.normalForce,
            normalForcePosition: load.normalForcePosition,
            normalForceEccentricity: load.normalForceEccentricity,
            uniformIntensity: load.uniformIntensity,
            startIntensity: load.startIntensity,
            endIntensity: load.endIntensity,
            startPosition: load.startPosition,
            endPosition: load.endPosition
          }))
        }
      }
      else
      {
        beamAnalysisRequest = {
          nodes: model.nodes.map(node => ({
            id: node.id,
            supportType: node.supportType,
            axialFixed: node.axialFixed,
            position: node.position,
            bearingLengthMm: node.bearingLengthMm
          })),
          spans: model.spans.map(span => ({
            id: span.id,
            leftNodeId: span.leftNodeId,
            rightNodeId: span.rightNodeId,
            length: span.length,
            e: span.E,
            a: span.A,
            i: span.I,
            loads: span.loads.map(load => ({
              type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
              category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
              pointLoad: load.pointLoad,
              pointLoadPosition: load.pointLoadPosition,
              normalForce: load.normalForce,
              normalForcePosition: load.normalForcePosition,
              normalForceEccentricity: load.normalForceEccentricity,
              uniformIntensity: load.uniformIntensity,
              startIntensity: load.startIntensity,
              endIntensity: load.endIntensity,
              startPosition: load.startPosition,
              endPosition: load.endPosition
            }))
          }))
        }
      }

      // Validate that materialDetails have been loaded from database
      if (!material.materialDetails || material.materialDetails.error) {
        alert(t('beam.analysis.alert.materialLoadFailed'))
        setLoading(false)
        return
      }

      let materialPayload
      try {
        materialPayload = resolveMaterialPayload({
          ...material,
          materialDetails: material.materialDetails
        })
      } catch (materialError) {
        console.error('Failed to resolve material payload:', materialError)
        alert(t('beam.analysis.alert.materialConfigError', { message: materialError instanceof Error ? materialError.message : String(materialError) }))
        setLoading(false)
        return
      }

      const crossSection = buildCrossSection(model)
      const loadCasesPayload = buildLoadCasesPayload(model)
      const loadCombinationsPayload: LoadCombinationStorageRequest[] = []
      
      // Build rebar zones payload for concrete
      const rebarZonesPayload = material.currentMaterial === 'concrete' && model.rebarZones 
        ? model.rebarZones.map(zone => ({
            start: zone.start,
            end: zone.end,
            cover: zone.cover,
            layers: zone.layers.map(layer => ({
              z: layer.z,
              diameter: layer.diameter,
              count: layer.count
            })),
            stirrups: zone.stirrups ? {
              diameter: zone.stirrups.diameter,
              spacing: zone.stirrups.spacing,
              legs: zone.stirrups.legs
            } : undefined
          }))
        : undefined

      // Build PT cables payload for basic analysis
      const ptCablesPayload = model.ptCables && model.ptCables.length > 0
        ? model.ptCables.map(cable => ({
            id: cable.id,
            prestressForce: cable.prestressForce,
            prestressLossRatio: cable.prestressLossRatio,
            // Strand-based inputs
            strandCount: cable.strandCount,
            strandAreaMm2: cable.strandAreaMm2,
            stressRatio: cable.stressRatio,
            maxAllowedStressMPa: cable.lossBreakdown?.maxAllowedStressMPa,
            // Tendon properties
            cableAreaMm2: cable.cableAreaMm2,
            frictionMu: cable.frictionMu,
            wobbleCoefficientK: cable.wobbleCoefficientK,
            anchorageSlipMm: cable.anchorageSlipMm,
            bondType: cable.bondType,
            nodeEccentricities: cable.nodeEccentricities,
            midSpanEccentricities: cable.midSpanEccentricities,
            // Loss breakdown (calculated in frontend)
            initialStressMPa: cable.lossBreakdown?.initialStressMPa,
            frictionLossKN: cable.lossBreakdown?.frictionLossKN,
            anchorageSlipLossKN: cable.lossBreakdown?.anchorageSlipLossKN,
            elasticShorteningLossKN: cable.lossBreakdown?.elasticShorteningLossKN,
            totalImmediateLossKN: cable.lossBreakdown?.totalImmediateLossKN,
            relaxationLossKN: cable.lossBreakdown?.relaxationLossKN,
            creepLossKN: cable.lossBreakdown?.creepLossKN,
            shrinkageLossKN: cable.lossBreakdown?.shrinkageLossKN,
            totalTimeDependentLossKN: cable.lossBreakdown?.totalTimeDependentLossKN,
            totalLossKN: cable.lossBreakdown?.totalLossKN,
            totalLossPercent: cable.lossBreakdown?.totalLossPercent
          }))
        : undefined

      const requestPayload = {
        ...beamAnalysisRequest,
        material: materialPayload,
        crossSection,
        codeRequirements: codeRequirements,
        limitState: selectedLimitState,
        designName: model.designName,
        designSessionId: model.designSessionId,
        loadCases: loadCasesPayload,
        loadCombinations: loadCombinationsPayload,
        concreteRebarZones: rebarZonesPayload,
        ptCables: ptCablesPayload,
        // Prestress effect for load factoring (EC2 2.4.2.2)
        prestressEffect: model.prestressEffect ?? 'Favorable'
      }

      const response = await fetch(`${beamApiBase(material.currentMaterial)}/analyze`, {
        method: 'POST',
        headers: getSessionHeaders(),
        body: JSON.stringify(requestPayload)
      })

      if (!response.ok)
      {
        throw new Error(await extractBackendError(response))
      }

      const analysisResult = await response.json()
      console.log('SMBeam analysis response:', analysisResult)

      if (isStale(revision, 'Analysis')) {
        return
      }
      setResult(analysisResult)
      setSelectedCombinationId('envelope')
      setActiveStep('results')
    }
    catch (error)
    {
      console.error('Analysis failed:', error)
      setResult({
        Success: false,
        Error: error instanceof Error ? error.message : 'Analysis failed',
        message: 'Failed to analyze beam. Please check your model and try again.'
      })
      setSelectedCombinationId('envelope')
    }
    finally
    {
      setLoading(false)
    }
  }, [model, material, codeRequirements, selectedLimitState, getSessionHeaders, setActiveStep])

  const analyzeAllLimitStates = useCallback(async () => {
    const revision: number = modelRevision.current
    setAnalyzingAll(true)
    try
    {
      const hasAxialLoads = model.spans.some(span =>
        span.loads.some(load => load.type === 'AxialForce' && Math.abs(load.normalForce) > 0.001)
      )

      if (hasAxialLoads)
      {
        const hasAxialSupport = model.nodes.some(node => node.axialFixed && node.supportType !== 'Free')
        if (!hasAxialSupport)
        {
          alert(t('beam.analysis.alert.axialNoSupport'))
          setAnalyzingAll(false)
          return
        }
      }

      // Build the analysis request (same as analyzeBeam, but without limit state - backend will handle all)
      const isSingleSpan = model.spans.length === 1
      let beamAnalysisRequest: any

      if (isSingleSpan)
      {
        const span = model.spans[0]
        const leftNode = model.nodes.find(node => node.id === span.leftNodeId)
        const rightNode = model.nodes.find(node => node.id === span.rightNodeId)

        beamAnalysisRequest = {
          length: span.length,
          e: span.E,
          a: span.A,
          i: span.I,
          leftSupportType: leftNode?.supportType || 'Free',
          rightSupportType: rightNode?.supportType || 'Free',
          leftBearingLengthMm: leftNode?.bearingLengthMm,
          rightBearingLengthMm: rightNode?.bearingLengthMm,
          loads: span.loads.map(load => ({
            type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
            category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
            pointLoad: load.pointLoad,
            pointLoadPosition: load.pointLoadPosition,
            normalForce: load.normalForce,
            normalForcePosition: load.normalForcePosition,
            normalForceEccentricity: load.normalForceEccentricity,
            uniformIntensity: load.uniformIntensity,
            startIntensity: load.startIntensity,
            endIntensity: load.endIntensity,
            startPosition: load.startPosition,
            endPosition: load.endPosition
          }))
        }
      }
      else
      {
        beamAnalysisRequest = {
          nodes: model.nodes.map(node => ({
            id: node.id,
            supportType: node.supportType,
            axialFixed: node.axialFixed,
            position: node.position,
            bearingLengthMm: node.bearingLengthMm
          })),
          spans: model.spans.map(span => ({
            id: span.id,
            leftNodeId: span.leftNodeId,
            rightNodeId: span.rightNodeId,
            length: span.length,
            e: span.E,
            a: span.A,
            i: span.I,
            loads: span.loads.map(load => ({
              type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
              category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
              pointLoad: load.pointLoad,
              pointLoadPosition: load.pointLoadPosition,
              normalForce: load.normalForce,
              normalForcePosition: load.normalForcePosition,
              normalForceEccentricity: load.normalForceEccentricity,
              uniformIntensity: load.uniformIntensity,
              startIntensity: load.startIntensity,
              endIntensity: load.endIntensity,
              startPosition: load.startPosition,
              endPosition: load.endPosition
            }))
          }))
        }
      }

      if (!material.materialDetails || material.materialDetails.error) {
        alert(t('beam.analysis.alert.materialLoadFailed'))
        setAnalyzingAll(false)
        return
      }

      const materialPayload = resolveMaterialPayload({
        ...material,
        materialDetails: material.materialDetails
      })
      const crossSection = buildCrossSection(model)

      const loadCasesPayload = buildLoadCasesPayload(model)
      const loadCombinationsPayload = model.loadCombinations && model.loadCombinations.length > 0
        ? model.loadCombinations
        : undefined

      // Build rebar zones payload for concrete (same shape as single-limit analysis)
      const rebarZonesPayload = material.currentMaterial === 'concrete' && model.rebarZones && model.rebarZones.length > 0
        ? model.rebarZones.map(zone => ({
            start: zone.start,
            end: zone.end,
            cover: zone.cover,
            layers: zone.layers.map(layer => ({
              z: layer.z,
              diameter: layer.diameter,
              count: layer.count
            })),
            stirrups: zone.stirrups ? {
              diameter: zone.stirrups.diameter,
              spacing: zone.stirrups.spacing,
              legs: zone.stirrups.legs
            } : undefined
          }))
        : undefined

      // Build PT cables payload
      const ptCablesPayloadAll = model.ptCables && model.ptCables.length > 0
        ? model.ptCables.map(cable => ({
            id: cable.id,
            prestressForce: cable.prestressForce,
            prestressLossRatio: cable.prestressLossRatio,
            // Strand-based inputs
            strandCount: cable.strandCount,
            strandAreaMm2: cable.strandAreaMm2,
            stressRatio: cable.stressRatio,
            maxAllowedStressMPa: cable.lossBreakdown?.maxAllowedStressMPa,
            // Tendon properties
            cableAreaMm2: cable.cableAreaMm2,
            frictionMu: cable.frictionMu,
            wobbleCoefficientK: cable.wobbleCoefficientK,
            anchorageSlipMm: cable.anchorageSlipMm,
            bondType: cable.bondType,
            nodeEccentricities: cable.nodeEccentricities,
            midSpanEccentricities: cable.midSpanEccentricities,
            // Loss breakdown (calculated in frontend)
            initialStressMPa: cable.lossBreakdown?.initialStressMPa,
            frictionLossKN: cable.lossBreakdown?.frictionLossKN,
            anchorageSlipLossKN: cable.lossBreakdown?.anchorageSlipLossKN,
            elasticShorteningLossKN: cable.lossBreakdown?.elasticShorteningLossKN,
            totalImmediateLossKN: cable.lossBreakdown?.totalImmediateLossKN,
            relaxationLossKN: cable.lossBreakdown?.relaxationLossKN,
            creepLossKN: cable.lossBreakdown?.creepLossKN,
            shrinkageLossKN: cable.lossBreakdown?.shrinkageLossKN,
            totalTimeDependentLossKN: cable.lossBreakdown?.totalTimeDependentLossKN,
            totalLossKN: cable.lossBreakdown?.totalLossKN,
            totalLossPercent: cable.lossBreakdown?.totalLossPercent
          }))
        : undefined

      const requestPayload = {
        ...beamAnalysisRequest,
        material: materialPayload,
        crossSection,
        codeRequirements: codeRequirements,
        designName: model.designName,
        designSessionId: model.designSessionId,
        loadCases: loadCasesPayload,
        loadCombinations: loadCombinationsPayload,
        concreteRebarZones: rebarZonesPayload,
        ptCables: ptCablesPayloadAll,
        // Prestress effect for load factoring (EC2 2.4.2.2)
        prestressEffect: model.prestressEffect ?? 'Favorable'
      }

      const response = await fetch(`${beamApiBase(material.currentMaterial)}/analyze-all`, {
        method: 'POST',
        headers: getSessionHeaders(),
        body: JSON.stringify(requestPayload)
      })

      if (!response.ok)
      {
        throw new Error(await extractBackendError(response))
      }

      const multiAnalysisResult = await response.json()
      console.log('Multi-limit-state analysis response:', multiAnalysisResult)

      if (isStale(revision, 'Multi-limit-state analysis')) {
        return
      }
      setMultiLimitStateResult(multiAnalysisResult)
      
      // Set the ULS result as the default displayed result
      const ulsResult = multiAnalysisResult.limitStateResults?.find((r: any) => r.limitState === 'ULS')
      if (ulsResult && ulsResult.success) {
        setResult({
          success: true,
          Success: true,
          analysisId: ulsResult.analysisId,
          results: ulsResult.results,
          Results: ulsResult.results,
          CombinationResults: ulsResult.combinationResults || [],
          combinationResults: ulsResult.combinationResults || [],
          beamInfo: ulsResult.results?.beamInfo
        })
      }
      
      setSelectedCombinationId('envelope')
      setActiveStep('results')
      
      // Count total number of load combinations across all limit states
      let totalCombinations = 0
      multiAnalysisResult.limitStateResults.forEach((ls: any) => {
        if (ls.success && ls.combinationResults) {
          totalCombinations += ls.combinationResults.length
        }
      })

      // Build detailed description of which combinations were analyzed per limit state
      const details: string[] = []
      multiAnalysisResult.limitStateResults.forEach((ls: any) => {
        if (!ls.success || !ls.combinationResults || ls.combinationResults.length === 0) {
          return
        }

        const lsName: string = ls.limitState || ls.LimitState || ''
        const combos: string[] = []

        ;(ls.combinationResults as any[]).forEach((c: any) => {
          const leading: string = c.leadingCategory || c.LeadingCategory || 'None'
          const factors: any[] = c.loadFactors || c.LoadFactors || []

          const factorParts: string[] = []
          for (let i = 0; i < (factors?.length ?? 0); i++) {
            const f = factors[i]
            const cat = f.category || f.Category || ''
            const gamma = typeof f.gamma === 'number' ? f.gamma.toFixed(2) : (typeof f.Gamma === 'number' ? f.Gamma.toFixed(2) : undefined)
            const psi = typeof f.psi === 'number' ? f.psi.toFixed(2) : (typeof f.Psi === 'number' ? f.Psi.toFixed(2) : undefined)
            const factor = typeof f.factor === 'number' ? f.factor.toFixed(2) : (typeof f.Factor === 'number' ? f.Factor.toFixed(2) : undefined)

            if (cat) {
              const parts: string[] = []
              parts.push(cat)
              if (gamma !== undefined) parts.push(`γ=${gamma}`)
              if (psi !== undefined) parts.push(`ψ=${psi}`)
              if (factor !== undefined) parts.push(`γ·ψ=${factor}`)
              factorParts.push(parts.join(' '))
            }
          }

          const baseLabel = leading === 'None' ? t('beam.results.noVariableLeading') : t('beam.analysis.summary.leading', { category: leading })
          const factorsLabel = factorParts.length > 0 ? ` [${factorParts.join(', ')}]` : ''
          combos.push(`${baseLabel}${factorsLabel}`)
        })

        if (combos.length > 0) {
          // One line for limit state, one line per combination in that state, then a blank line separator
          details.push(lsName)
          for (let k = 0; k < combos.length; k++) {
            details.push(combos[k])
          }
          details.push('')
        }
      })

      const summaryLines: string[] = [
        t('beam.analysis.summary.allAnalyzed', { count: totalCombinations })
      ]

      if (details.length > 0) {
        summaryLines.push('')
        summaryLines.push(t('beam.analysis.summary.combinations'))
        summaryLines.push(...details)
      }

      setLastAnalysisSummary(summaryLines)
      // Backend-authoritative theory: analyze-all routes timber to Timoshenko (shear deformation
      // is always included for timber SLS), so read the theory from the response.
      setAnalysisTheory(multiAnalysisResult.analysisTheory === 'Timoshenko' ? 'Timoshenko' : 'EulerBernoulli')
    }
    catch (error)
    {
      console.error('Multi-limit-state analysis failed:', error)
      alert(t('beam.analysis.alert.analyzeAllFailed', { message: error instanceof Error ? error.message : String(error) }))
      setMultiLimitStateResult(null)
    }
    finally
    {
      setAnalyzingAll(false)
    }
  }, [model, material, codeRequirements, getSessionHeaders, setActiveStep])

  // Timoshenko beam theory analysis for ALL limit states (ULS + SLS variants)
  // This mirrors the Euler-Bernoulli analyze-all but uses Timoshenko theory
  // For indeterminate beams: different reactions, moments, and shear forces
  // For all beams: larger deflections due to shear contribution
  const analyzeTimoshenko = useCallback(async () => {
    const revision: number = modelRevision.current
    setAnalyzingTimoshenko(true)
    try
    {
      const hasAxialLoads = model.spans.some(span =>
        span.loads.some(load => load.type === 'AxialForce' && Math.abs(load.normalForce) > 0.001)
      )

      if (hasAxialLoads)
      {
        const hasAxialSupport = model.nodes.some(node => node.axialFixed && node.supportType !== 'Free')
        if (!hasAxialSupport)
        {
          alert(t('beam.analysis.alert.axialNoSupport'))
          setAnalyzingTimoshenko(false)
          return
        }
      }

      const isSingleSpan = model.spans.length === 1
      let beamAnalysisRequest: any

      if (isSingleSpan)
      {
        const span = model.spans[0]
        const leftNode = model.nodes.find(node => node.id === span.leftNodeId)
        const rightNode = model.nodes.find(node => node.id === span.rightNodeId)

        beamAnalysisRequest = {
          length: span.length,
          e: span.E,
          a: span.A,
          i: span.I,
          leftSupportType: leftNode?.supportType || 'Free',
          rightSupportType: rightNode?.supportType || 'Free',
          leftBearingLengthMm: leftNode?.bearingLengthMm,
          rightBearingLengthMm: rightNode?.bearingLengthMm,
          loads: span.loads.map(load => ({
            type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
            category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
            pointLoad: load.pointLoad,
            pointLoadPosition: load.pointLoadPosition,
            normalForce: load.normalForce,
            normalForcePosition: load.normalForcePosition,
            normalForceEccentricity: load.normalForceEccentricity,
            uniformIntensity: load.uniformIntensity,
            startIntensity: load.startIntensity,
            endIntensity: load.endIntensity,
            startPosition: load.startPosition,
            endPosition: load.endPosition
          }))
        }
      }
      else
      {
        beamAnalysisRequest = {
          nodes: model.nodes.map(node => ({
            id: node.id,
            supportType: node.supportType,
            axialFixed: node.axialFixed,
            position: node.position,
            bearingLengthMm: node.bearingLengthMm
          })),
          spans: model.spans.map(span => ({
            id: span.id,
            leftNodeId: span.leftNodeId,
            rightNodeId: span.rightNodeId,
            length: span.length,
            e: span.E,
            a: span.A,
            i: span.I,
            loads: span.loads.map(load => ({
              type: load.type === 'AxialForce' ? 'NormalForceLoad' : load.type,
              category: load.category || (load.id.includes('selfweight') ? 'Permanent' : 'Variable'),
              pointLoad: load.pointLoad,
              pointLoadPosition: load.pointLoadPosition,
              normalForce: load.normalForce,
              normalForcePosition: load.normalForcePosition,
              normalForceEccentricity: load.normalForceEccentricity,
              uniformIntensity: load.uniformIntensity,
              startIntensity: load.startIntensity,
              endIntensity: load.endIntensity,
              startPosition: load.startPosition,
              endPosition: load.endPosition
            }))
          }))
        }
      }

      if (!material.materialDetails || material.materialDetails.error) {
        alert(t('beam.analysis.alert.materialLoadFailed'))
        setAnalyzingTimoshenko(false)
        return
      }

      const materialPayload = resolveMaterialPayload({
        ...material,
        materialDetails: material.materialDetails
      })
      const crossSection = buildCrossSection(model)
      const loadCasesPayload = buildLoadCasesPayload(model)
      const loadCombinationsPayload = model.loadCombinations && model.loadCombinations.length > 0
        ? model.loadCombinations
        : undefined

      const rebarZonesPayload = material.currentMaterial === 'concrete' && model.rebarZones && model.rebarZones.length > 0
        ? model.rebarZones.map(zone => ({
            start: zone.start,
            end: zone.end,
            cover: zone.cover,
            layers: zone.layers.map(layer => ({
              z: layer.z,
              diameter: layer.diameter,
              count: layer.count
            })),
            stirrups: zone.stirrups ? {
              diameter: zone.stirrups.diameter,
              spacing: zone.stirrups.spacing,
              legs: zone.stirrups.legs
            } : undefined
          }))
        : undefined

      // Build PT cables payload for Timoshenko
      const ptCablesPayloadTimoshenko = model.ptCables && model.ptCables.length > 0
        ? model.ptCables.map(cable => ({
            id: cable.id,
            prestressForce: cable.prestressForce,
            prestressLossRatio: cable.prestressLossRatio,
            // Strand-based inputs
            strandCount: cable.strandCount,
            strandAreaMm2: cable.strandAreaMm2,
            stressRatio: cable.stressRatio,
            maxAllowedStressMPa: cable.lossBreakdown?.maxAllowedStressMPa,
            // Tendon properties
            cableAreaMm2: cable.cableAreaMm2,
            frictionMu: cable.frictionMu,
            wobbleCoefficientK: cable.wobbleCoefficientK,
            anchorageSlipMm: cable.anchorageSlipMm,
            bondType: cable.bondType,
            nodeEccentricities: cable.nodeEccentricities,
            midSpanEccentricities: cable.midSpanEccentricities,
            // Loss breakdown (calculated in frontend)
            initialStressMPa: cable.lossBreakdown?.initialStressMPa,
            frictionLossKN: cable.lossBreakdown?.frictionLossKN,
            anchorageSlipLossKN: cable.lossBreakdown?.anchorageSlipLossKN,
            elasticShorteningLossKN: cable.lossBreakdown?.elasticShorteningLossKN,
            totalImmediateLossKN: cable.lossBreakdown?.totalImmediateLossKN,
            relaxationLossKN: cable.lossBreakdown?.relaxationLossKN,
            creepLossKN: cable.lossBreakdown?.creepLossKN,
            shrinkageLossKN: cable.lossBreakdown?.shrinkageLossKN,
            totalTimeDependentLossKN: cable.lossBreakdown?.totalTimeDependentLossKN,
            totalLossKN: cable.lossBreakdown?.totalLossKN,
            totalLossPercent: cable.lossBreakdown?.totalLossPercent
          }))
        : undefined

      const requestPayload = {
        ...beamAnalysisRequest,
        material: materialPayload,
        crossSection,
        codeRequirements: codeRequirements,
        designName: model.designName,
        designSessionId: model.designSessionId,
        loadCases: loadCasesPayload,
        loadCombinations: loadCombinationsPayload,
        concreteRebarZones: rebarZonesPayload,
        ptCables: ptCablesPayloadTimoshenko,
        // Prestress effect for load factoring (EC2 2.4.2.2)
        prestressEffect: model.prestressEffect ?? 'Favorable'
      }

      // Call the new analyze-all-timoshenko endpoint (mirrors analyze-all)
      const response = await fetch(`${beamApiBase(material.currentMaterial)}/analyze-all-timoshenko`, {
        method: 'POST',
        headers: getSessionHeaders(),
        body: JSON.stringify(requestPayload)
      })

      if (!response.ok)
      {
        throw new Error(await extractBackendError(response))
      }

      const multiAnalysisResult = await response.json()
      console.log('Timoshenko multi-limit-state analysis response:', multiAnalysisResult)

      if (isStale(revision, 'Timoshenko analysis')) {
        return
      }
      setMultiLimitStateResult(multiAnalysisResult)
      
      // Set the ULS result as the default displayed result (same as Euler-Bernoulli)
      const ulsResult = multiAnalysisResult.limitStateResults?.find((r: any) => r.limitState === 'ULS')
      if (ulsResult && ulsResult.success) {
        setResult({
          success: true,
          Success: true,
          analysisId: ulsResult.analysisId,
          results: ulsResult.results,
          Results: ulsResult.results,
          CombinationResults: ulsResult.combinationResults || [],
          combinationResults: ulsResult.combinationResults || [],
          beamInfo: ulsResult.results?.beamInfo,
          analysisTheory: 'Timoshenko'
        })
      }
      
      setSelectedCombinationId('envelope')
      setActiveStep('results')
      
      // Count total number of load combinations across all limit states
      let totalCombinations = 0
      multiAnalysisResult.limitStateResults.forEach((ls: any) => {
        if (ls.success && ls.combinationResults) {
          totalCombinations += ls.combinationResults.length
        }
      })

      // Build detailed description of combinations (same format as Euler-Bernoulli)
      const details: string[] = []
      multiAnalysisResult.limitStateResults.forEach((ls: any) => {
        if (!ls.success || !ls.combinationResults || ls.combinationResults.length === 0) {
          return
        }

        const lsName: string = ls.limitState || ls.LimitState || ''
        const combos: string[] = []

        ;(ls.combinationResults as any[]).forEach((c: any) => {
          const leading: string = c.leadingCategory || c.LeadingCategory || 'None'
          const factors: any[] = c.loadFactors || c.LoadFactors || []

          const factorParts: string[] = []
          for (let i = 0; i < (factors?.length ?? 0); i++) {
            const f = factors[i]
            const cat = f.category || f.Category || ''
            const gamma = typeof f.gamma === 'number' ? f.gamma.toFixed(2) : (typeof f.Gamma === 'number' ? f.Gamma.toFixed(2) : undefined)
            const psi = typeof f.psi === 'number' ? f.psi.toFixed(2) : (typeof f.Psi === 'number' ? f.Psi.toFixed(2) : undefined)
            const factor = typeof f.factor === 'number' ? f.factor.toFixed(2) : (typeof f.Factor === 'number' ? f.Factor.toFixed(2) : undefined)

            if (cat) {
              const parts: string[] = []
              parts.push(cat)
              if (gamma !== undefined) parts.push(`γ=${gamma}`)
              if (psi !== undefined) parts.push(`ψ=${psi}`)
              if (factor !== undefined) parts.push(`γ·ψ=${factor}`)
              factorParts.push(parts.join(' '))
            }
          }

          const leadingLabel = leading === 'None' ? t('beam.results.noVariableLeading') : t('beam.analysis.summary.leading', { category: leading })
          const factorSummary = factorParts.length > 0 ? ` [${factorParts.join(', ')}]` : ''
          combos.push(`${leadingLabel}${factorSummary}`)
        })

        details.push(lsName)
        combos.forEach(combo => details.push(combo))
      })

      // Calculate slenderness ratio for additional context
      const totalLength = model.spans.reduce((sum, span) => sum + span.length, 0)
      const height = model.crossSection.height / 1000
      const slendernessRatio = height > 0 ? totalLength / height : 0

      // Build summary with same format as Euler-Bernoulli but with Timoshenko note
      const shearSignificance = slendernessRatio < 10 ? t('beam.analysis.summary.shearSignificant') : slendernessRatio > 20 ? t('beam.analysis.summary.shearMinimal') : t('beam.analysis.summary.shearModerate')
      const summaryLines: string[] = [
        t('beam.analysis.summary.timoshenkoAnalyzed', { count: totalCombinations }),
        t('beam.analysis.summary.slenderness', { ratio: slendernessRatio.toFixed(1), significance: shearSignificance })
      ]

      if (details.length > 0) {
        summaryLines.push('')
        summaryLines.push(t('beam.analysis.summary.combinations'))
        summaryLines.push(...details)
      }

      setLastAnalysisSummary(summaryLines)
      setAnalysisTheory('Timoshenko')
    }
    catch (error)
    {
      console.error('Timoshenko analysis failed:', error)
      alert(t('beam.analysis.alert.timoshenkoFailed', { message: error instanceof Error ? error.message : String(error) }))
      setMultiLimitStateResult(null)
    }
    finally
    {
      setAnalyzingTimoshenko(false)
    }
  }, [model, material, codeRequirements, getSessionHeaders, setActiveStep])

  const calculateResponse = useCallback(async () => {
    if (!result || !(result.Success || result.success)) {
      console.error('Cannot calculate response: analysis not completed successfully')
      return
    }

    const analysisId = resolveAnalysisId(result)
    if (!analysisId) {
      alert(t('beam.analysis.alert.missingAnalysisId'))
      return
    }

    const revision: number = modelRevision.current
    setCalculatingResponse(true)
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), 30000)

    try
    {
      if (!material.materialDetails || material.materialDetails.error) {
        alert(t('beam.analysis.alert.materialLoadFailed'))
        setCalculatingResponse(false)
        return
      }

      const materialPayload = resolveMaterialPayload({
        ...material,
        materialDetails: material.materialDetails
      })
      const crossSection = buildCrossSection(model)

      let response: Response

      try
      {
        // If we have a multi-limit-state analysis group, calculate responses for ALL limit states
        if (multiLimitStateResult && multiLimitStateResult.parentAnalysisId)
        {
          const multiRequestBody: any = {
            parentAnalysisId: multiLimitStateResult.parentAnalysisId,
            crossSection,
            material: materialPayload,
            codeRequirements: codeRequirements,
            spans: null,
            nodes: null,
            bucklingLengthY: undefined,
            bucklingLengthZ: undefined,
            bucklingLengthLT: undefined
          }

          console.log('Calculating multi-limit-state response...', multiRequestBody)

          response = await fetch('/api/Response/calculate-all-limit-states', {
            method: 'POST',
            headers: getSessionHeaders(),
            body: JSON.stringify(multiRequestBody),
            signal: controller.signal
          })
        }
        else
        {
          // Single-analysis fallback (legacy path)
          const requestBody: Record<string, unknown> = {
            analysisId
          }

          console.log('Calculating response...', requestBody)

          response = await fetch('/api/Response/calculate', {
            method: 'POST',
            headers: getSessionHeaders(),
            body: JSON.stringify(requestBody),
            signal: controller.signal
          })
        }
      }
      finally
      {
        window.clearTimeout(timeoutId)
      }

      console.log('Response status:', response.status, response.statusText)
      console.log('Response headers:', [...response.headers.entries()])

      if (!response.ok)
      {
        console.error('❌ Response NOT OK:', response.status, response.statusText)
        throw new Error(await extractBackendError(response))
      }

      console.log('✅ Response OK (status 200)')
      const rawResult: any = await response.json()
      console.log('📊 Response result:', rawResult)

      if (isStale(revision, 'Response calculation')) {
        return
      }

      // Multi-limit-state response path
      if (multiLimitStateResult && multiLimitStateResult.parentAnalysisId)
      {
        const multiResponseResult: any = rawResult
        const isMultiSuccess = multiResponseResult.success === true || multiResponseResult.Success === true
        if (!isMultiSuccess)
        {
          const errorMsg = multiResponseResult.error || multiResponseResult.Error || 'Multi-limit-state response calculation failed'
          console.error('Multi-limit-state response not successful:', multiResponseResult)
          throw new Error(errorMsg)
        }

        setMultiLimitStateResponseResult(multiResponseResult)

        const limitStateResponses: any[] = multiResponseResult.limitStateResponses || multiResponseResult.LimitStateResponses || []

        // Collect all combination responses across all limit states
        const allCombinationResponses: any[] = []
        let ulsData: any = null
        const allGlobalChecks: any[] = []

        for (let i = 0; i < limitStateResponses.length; i++)
        {
          const lsResp = limitStateResponses[i]
          const lsName: string = lsResp.limitState || lsResp.LimitState || ''

          const lsCombinations: any[] = lsResp.combinationResponses || lsResp.CombinationResponses || []
          for (let j = 0; j < lsCombinations.length; j++)
          {
            allCombinationResponses.push(lsCombinations[j])
          }

          const lsData: any = lsResp.data || lsResp.Data

          if (!ulsData && lsName === 'ULS' && (lsResp.data || lsResp.Data))
          {
            ulsData = lsData
          }

          // Collect GlobalChecks (ULS + all SLS limit states)
          if (lsData && (lsData.GlobalChecks || (lsData as any).globalChecks))
          {
            const lsChecks: any[] = lsData.GlobalChecks || (lsData as any).globalChecks
            for (let k = 0; k < lsChecks.length; k++)
            {
              allGlobalChecks.push(lsChecks[k])
            }
          }
        }

        if (ulsData)
        {
          setResult((prev: any) => ({
            ...prev,
            ResponseData: {
              ...(ulsData || {}),
              // Merge ULS + all SLS global checks so the report can show both ULS and SLS tables
              GlobalChecks: allGlobalChecks,
              CombinationResponses: allCombinationResponses,
              combinationResponses: allCombinationResponses
            }
          }))

          console.log('✅ Attached ULS response data with', allCombinationResponses.length, 'combination responses (all limit states)')
        }

        setActiveStep('response')
        console.log('✅ Multi-limit-state response calculation completed successfully')
        return
      }

      // Single-analysis response path
      const responseResult: any = rawResult
      const isSuccess = responseResult.success === true || responseResult.Success === true
      const responseId: string | undefined = responseResult.responseId || responseResult.ResponseId
      const responseData = responseResult.data || responseResult.Data
      const combinationResponses = responseResult.combinationResponses || responseResult.CombinationResponses

      console.log('📊 Response has CombinationResponses:', combinationResponses ? combinationResponses.length : 0)

      if (isSuccess && (responseData || responseId))
      {
        let finalData = responseData
        let finalCombinationResponses = combinationResponses

        // Prefer fetching from storage if ResponseId is provided
        if (!finalData && responseId)
        {
          try
          {
            const { fetchResponseData } = await import('../utils/analysisApi')
            const stored = await fetchResponseData(responseId, getSessionHeaders)
            if (stored && (stored.data || (stored as any).Data))
            {
              finalData = stored.data || (stored as any).Data
              // Combination responses, when present, will be nested inside the stored data payload
              const storedCombinations = (finalData as any).combinationResponses || (finalData as any).CombinationResponses
              finalCombinationResponses = storedCombinations || finalCombinationResponses
            }
          }
          catch (fetchErr)
          {
            console.warn('Failed to fetch stored response by ResponseId:', fetchErr)
          }
        }

        if (finalData)
        {
          setResult((prev: any) => ({
            ...prev,
            ResponseData: {
              ...finalData,
              CombinationResponses: finalCombinationResponses,
              combinationResponses: finalCombinationResponses
            }
          }))
          console.log('✅ Response data set with', finalCombinationResponses?.length || 0, 'combination responses')
        }

        try
        {
          await fetch('/api/Session/step', {
            method: 'POST',
            headers: getSessionHeaders(),
            body: JSON.stringify({ step: 'response', payload: responseData })
          })
        }
        catch (sessionError)
        {
          console.warn('Failed to record response session step:', sessionError)
        }

        setActiveStep('response')
        console.log('✅ Response calculation completed successfully')
      }
      else
      {
        const errorMsg = responseResult.error || responseResult.Error || 'Response calculation failed'
        console.error('Response not successful:', responseResult)
        throw new Error(errorMsg)
      }
    }
    catch (error)
    {
      if (error instanceof Error && error.name === 'AbortError')
      {
        console.error('Response calculation timed out (30s)')
        alert(t('beam.analysis.alert.responseTimeout'))
      }
      else
      {
        console.error('Response calculation failed:', error)
        alert(t('beam.analysis.alert.responseFailed', { message: error instanceof Error ? error.message : String(error) }))
      }
    }
    finally
    {
      setCalculatingResponse(false)
    }
  }, [result, model, getSessionHeaders, setActiveStep, multiLimitStateResult, material, codeRequirements])

  const calculateSecondOrderEffects = useCallback(async (options?: { combinationId?: string }) => {
    if (!result || !(result.Success || result.success))
    {
      console.error('Cannot calculate second-order effects: analysis not completed successfully')
      return
    }

    const analysisId = resolveAnalysisId(result)
    setCalculatingSecondOrderEffects(true)

    try
    {
      if (!material.materialDetails || material.materialDetails.error) {
        alert(t('beam.analysis.alert.materialLoadFailed'))
        setCalculatingSecondOrderEffects(false)
        return
      }

      const materialPayload = resolveMaterialPayload({
        ...material,
        materialDetails: material.materialDetails
      })
      const crossSection = buildCrossSection(model)
      const spans = model.spans && model.spans.length > 0 ? model.spans : null
      const nodes = model.nodes && model.nodes.length > 0 ? model.nodes : null

      const requestBody: any = {
        limitState: selectedLimitState
      }

      if (options && options.combinationId && options.combinationId.length > 0)
      {
        requestBody.combinationId = options.combinationId
      }

      if (analysisId)
      {
        requestBody.analysisId = analysisId
      }
      else
      {
        requestBody.analysisResult = result
        requestBody.crossSection = crossSection
        requestBody.material = materialPayload
        requestBody.codeRequirements = codeRequirements
        requestBody.spans = spans
        requestBody.nodes = nodes
      }

      console.log('Calculating second-order effects...', requestBody)

      const response = await fetch('/api/SecondOrderEffects/calculate', {
        method: 'POST',
        headers: getSessionHeaders(),
        body: JSON.stringify(requestBody)
      })

      if (!response.ok)
      {
        const errorText = await response.text()
        console.error('❌ Second-order effects calculation failed:', response.status, response.statusText)
        console.error('Error:', errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const bucklingResult = await response.json()
      console.log('✅ Second-order effects calculated:', bucklingResult)

      if (bucklingResult.success)
      {
        setSecondOrderEffectsResult(bucklingResult.overallResults)
      }
      else
      {
        throw new Error(bucklingResult.error || 'Buckling analysis failed')
      }
    }
    catch (error)
    {
      console.error('Second-order effects calculation failed:', error)
      alert(t('beam.analysis.alert.secondOrderFailed', { message: error instanceof Error ? error.message : String(error) }))
    }
    finally
    {
      setCalculatingSecondOrderEffects(false)
    }
  }, [result, material, model, codeRequirements, selectedLimitState, getSessionHeaders])

  const calculateDynamics = useCallback(async () => {
    if (!result || !(result.Success || result.success))
    {
      console.error('Cannot calculate dynamics: analysis not completed successfully')
      return
    }

    const analysisId = resolveAnalysisId(result)
    setCalculatingDynamics(true)

    try
    {
      // The backend derives geometry, material, EI and the vibrating PERMANENT mass from the
      // stored analysis (single canonical path) — only the analysis id and options travel.
      if (!analysisId)
      {
        alert(t('beam.analysis.alert.dynamicsNeedsAnalysis'))
        setCalculatingDynamics(false)
        return
      }

      const requestBody: Record<string, unknown> = {
        analysisId,
        analysisType: 'NaturalFrequencies',
        buildingUse: 'Residential',
        numberOfModes: 5
      }

      console.log('Calculating dynamics...', requestBody)

      const response = await fetch('/api/Dynamics/calculate', {
        method: 'POST',
        headers: getSessionHeaders(),
        body: JSON.stringify(requestBody)
      })

      if (!response.ok)
      {
        const errorText = await response.text()
        console.error('❌ Dynamics calculation failed:', response.status, response.statusText)
        console.error('Error:', errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const dynamicsResponse = await response.json()
      console.log('✅ Dynamics calculated:', dynamicsResponse)

      if (dynamicsResponse.success)
      {
        setDynamicsResult(dynamicsResponse.results)
      }
      else
      {
        throw new Error(dynamicsResponse.error || 'Dynamics analysis failed')
      }
    }
    catch (error)
    {
      console.error('Dynamics calculation failed:', error)
      alert(t('beam.analysis.alert.dynamicsFailed', { message: error instanceof Error ? error.message : String(error) }))
    }
    finally
    {
      setCalculatingDynamics(false)
    }
  }, [result, material, model, getSessionHeaders])

  // Auto-switch result when user selects different limit state
  useEffect(() => {
    if (multiLimitStateResult && multiLimitStateResult.limitStateResults) {
      const limitStateData = multiLimitStateResult.limitStateResults.find(
        (ls) => ls.limitState === selectedLimitState
      )
      if (limitStateData && limitStateData.success && limitStateData.results) {
        setResult({
          success: true,
          Success: true,
          analysisId: limitStateData.analysisId,
          results: limitStateData.results,
          Results: limitStateData.results,
          CombinationResults: limitStateData.combinationResults || [],
          combinationResults: limitStateData.combinationResults || [],
          beamInfo: limitStateData.results?.beamInfo
        })
        console.log(`📊 Switched to ${selectedLimitState} results`)
      }
    }
  }, [selectedLimitState, multiLimitStateResult])

  return {
    result,
    setResult,
    invalidateResults,
    loading,
    analyzeBeam,
    analyzingAll,
    analyzeAllLimitStates,
    analyzingTimoshenko,
    analyzeTimoshenko,
    analysisTheory,
    multiLimitStateResult,
    lastAnalysisSummary,
    selectedCombinationId,
    setSelectedCombinationId,
    calculatingResponse,
    calculateResponse,
    calculatingSecondOrderEffects,
    calculateSecondOrderEffects,
    secondOrderEffectsResult,
    calculatingDynamics,
    calculateDynamics,
    dynamicsResult
  }
}
